import findLast from 'lodash/findLast';
import { createSelector } from 'reselect';
import { fallbackToEmptyArray } from '../utils/fallbackHelper';

const selectAllItemIds = cartItems =>
  fallbackToEmptyArray(cartItems.cartItemIds);

const selectHasItems = cartItems =>
  cartItems.cartItemIds && cartItems.cartItemIds.length > 0;

const selectCartItemsCount = cartItems =>
  cartItems.cartItemIds ? cartItems.cartItemIds.length : 0;

// itemDisplayGroups are containers of items that should be displayed together.
// Individual items with no groupId belong to their own itemDisplayGroup.
// Lists of items with the same groupId belong to their own itemDisplayGroup.
const selectCartItemDisplayGroups = createSelector(
  cartItems => cartItems,
  cartItems => {
    const cartItemDisplayGroups = [];

    Object.values(cartItems.cartItems).forEach(cartItem => {
      if (cartItem) {
        if (cartItem.groupId) {
          const groupDisplayIndex = cartItemDisplayGroups.findIndex(
            itemGroup => itemGroup.groupId === cartItem.groupId
          );
          if (groupDisplayIndex !== -1) {
            // add itemId to existing itemGrouping
            cartItemDisplayGroups[groupDisplayIndex].cartItemIds.push(
              cartItem.id
            );
          } else {
            // create new itemGrouping
            cartItemDisplayGroups.push({
              groupId: cartItem.groupId,
              cartItemIds: [cartItem.id],
            });
          }
        } else {
          // standalone item
          cartItemDisplayGroups.push({ cartItemIds: [cartItem.id] });
        }
      }
    });

    return cartItemDisplayGroups;
  }
);

const selectCartItemDisplayGroupsCount = cartItems =>
  selectCartItemDisplayGroups(cartItems).length || 0;

const selectWholeItemByItemId = (cartItems, itemId) =>
  cartItems.cartItems[itemId] || {};

const selectMerchandisingDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).merchandisingData || {};

const selectProductDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).productData || {};

const selectFulfillmentDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).fulfillmentData || {};

const selectFulfillmentSkuByItemId = (cartItems, itemId) =>
  selectFulfillmentDataByItemId(cartItems, itemId).fulfillmentSku;

const selectFulfillmentSkuVersionByItemId = (cartItems, itemId) =>
  selectFulfillmentDataByItemId(cartItems, itemId).mcpProductVersion;

const selectDesignDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).designData || {};

const selectAnalyticsDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).analyticsData || {};

const selectFinanceDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).financeData || {};

const selectUIDataByItemId = (cartItems, itemId) =>
  selectWholeItemByItemId(cartItems, itemId).ui || {};

const selectProductNameByItemId = (cartItems, itemId) =>
  selectMerchandisingDataByItemId(cartItems, itemId).productName;

const selectCustomDisplayAttributesByItemId = (cartItems, itemId) =>
  selectMerchandisingDataByItemId(cartItems, itemId).customDisplayAttributes;

const selectProductKeyByItemId = (cartItems, itemId) =>
  selectProductDataByItemId(cartItems, itemId).productKey;

const selectProductVersionByItemId = (cartItems, itemId) =>
  selectProductDataByItemId(cartItems, itemId).productVersion;

const selectPreviewUrlByItemId = (cartItems, itemId) =>
  selectDesignDataByItemId(cartItems, itemId).livePreviewUrl;

const selectQuantityByItemId = (cartItems, itemId) =>
  selectFulfillmentDataByItemId(cartItems, itemId).fulfilledQuantity;

const selectBlockUserInteractionByItemId = (cartItems, itemId) =>
  selectUIDataByItemId(cartItems, itemId).blockUserInteraction || false;

const selectItemUpdateInProgressByItemId = (cartItems, itemId) =>
  selectUIDataByItemId(cartItems, itemId).itemUpdateInProgress || false;

const selectErrorByItemId = (cartItems, itemId) =>
  selectUIDataByItemId(cartItems, itemId).error;

const selectMerchandisableQuantitiesV2 = (cartItems, itemId) =>
  selectMerchandisingDataByItemId(cartItems, itemId)
    .merchandisableQuantitiesV2 || undefined;

const selectProductAttributes = (cartItems, itemId) =>
  fallbackToEmptyArray(
    selectMerchandisingDataByItemId(cartItems, itemId).productAttributes
  );

const selectEditOptionsUrl = (cartItems, itemId) =>
  selectMerchandisingDataByItemId(cartItems, itemId).editOptionsUrl ||
  undefined;

const selectProductAttributeList = (cartItems, itemId) =>
  fallbackToEmptyArray(
    selectProductDataByItemId(cartItems, itemId).selectedAttributes
  );

const selectEditDesignUrl = (cartItems, itemId) =>
  selectDesignDataByItemId(cartItems, itemId).editDocumentUrl || undefined;

const selectDocumentRefUrl = (cartItems, itemId) =>
  selectDesignDataByItemId(cartItems, itemId).documentReferenceUrl || undefined;

const selectPurcsDataByItemId = (cartItems, itemId) =>
  selectDesignDataByItemId(cartItems, itemId).purcsData || {};

const selectAreAnyItemsBlockingUserInteraction = state => {
  const cartItemIds = state.cartItems && state.cartItems.cartItemIds;
  if (cartItemIds) {
    return cartItemIds.some(cartItemId =>
      selectBlockUserInteractionByItemId(state.cartItems, cartItemId)
    );
  }
};

const selectAreAnySpecificItemsBlockingUserInteraction = (cartItems, itemIds) =>
  itemIds.some(id => selectBlockUserInteractionByItemId(cartItems, id));

const selectItemsWithLegalRequirements = state => {
  const cartItemIds = state.cartItems?.cartItemIds || [];
  return cartItemIds
    .map(itemId => selectWholeItemByItemId(state.cartItems, itemId))
    .filter(item => item.legalRequirements && item.legalRequirements.length);
};

const selectSuppressDefaultGroupDisplay = (cartItems, groupId) => {
  const groupData = cartItems.lineItemGroupData[groupId] || {};
  return (
    groupData?.customOrderData?.modData?.host?.cart
      ?.suppressDefaultGroupDisplay || false
  );
};

const selectProductNamesForItemIds = (cartItems, itemIds) =>
  itemIds
    .filter(
      itemId =>
        !!(
          cartItems.cartItems &&
          cartItems.cartItems[itemId]?.merchandisingData?.productName
        )
    )
    .map(itemId => cartItems.cartItems[itemId].merchandisingData.productName);

/**
 * @param {*} cartItems cartItems slice of Redux State
 * @param {*} groupId The item group id to lookup the title for
 * @param {*} locale The locale to find the title in for multi-lingual support.
 *
 * Returns the display title for the group.
 *
 * The display title can be found based on multiple values from the Vistacart API,
 * the goal of this function is to pick the best localized display value.
 * https://vistaprint.atlassian.net/wiki/spaces/OMT/pages/761005580/Using+Item+Groups
 *
 * The order of operations is important:
 * 1. First see if a display title is localized via the `display` bucket's override schema(overrideTitle).
 * 2. If no localized display title override exists, use the `default` display value(defaultDisplayTitle).
 * 3. If no `display` configuration has been set, fallback to prior display data API in `modData`(legacyGroupTitleValue).
 * 4. If no display titles have been found, the baseline experience will be used - no title (undefined returned).
 */
const selectItemGroupTitle = (cartItems, groupId, locale) => {
  const groupData = cartItems.lineItemGroupData[groupId] || {};
  const overrideConfig = groupData?.customOrderData?.display?.overrides || [];
  const localeOverrides = findLast(overrideConfig, override =>
    override.locales.includes(locale)
  );
  const overrideTitle = localeOverrides?.strings?.itemGroupTitle || null;
  const defaultDisplayTitle =
    groupData?.customOrderData?.display?.strings?.itemGroupTitle || null;
  const legacyGroupTitleValue =
    groupData?.customOrderData?.modData?.host?.cart?.itemGroupTitle;
  return (
    overrideTitle || defaultDisplayTitle || legacyGroupTitleValue || undefined
  );
};

/**
 * @param {*} cartItems cartItems slice of Redux State
 * @param {*} groupId The item group id to lookup the title for
 * @param {*} locale The locale to find the title in for multi-lingual support.
 *
 * Returns the display total label for the group.
 *
 * The display total label can be found based on multiple values from the Vistacart API,
 * the goal of this function is to pick the best localized display value.
 * https://vistaprint.atlassian.net/wiki/spaces/OMT/pages/761005580/Using+Item+Groups
 *
 * The order of operations is important:
 * 1. First see if a display total label is localized via the `display` bucket's override schema(overrideTotalLabel).
 * 2. If no localized display total label override exists, use the `default` display value(defaultTotalLabel).
 * 3. If no `display` configuration has been set, fallback to prior display data API in `modData`(legacyGroupTotalLabel).
 * 4. If no display titles have been found, the baseline experience will be used - "Group Total:" (undefined returned).
 */
const selectItemGroupTotalLabel = (cartItems, groupId, locale) => {
  const groupData = cartItems.lineItemGroupData[groupId] || {};
  const overrideConfig = groupData?.customOrderData?.display?.overrides || [];
  const localeOverrides = findLast(overrideConfig, override =>
    override.locales.includes(locale)
  );
  const overrideTotalLabel =
    localeOverrides?.strings?.itemGroupTotalLabel || null;
  const defaultTotalLabel =
    groupData?.customOrderData?.display?.strings?.itemGroupTotalLabel || null;
  const legacyGroupTotalLabel =
    groupData?.customOrderData?.modData?.host?.cart?.itemGroupTotalLabel;
  return (
    overrideTotalLabel ||
    defaultTotalLabel ||
    legacyGroupTotalLabel ||
    undefined
  );
};

const selectCartContainsSubscription = state =>
  state.cartItems && state.cartItems.hasDeferredPaymentItems;

const selectCartItemsMap = cartItems => cartItems.cartItems || {};

export default {
  selectAllItemIds,
  selectCartItemsCount,
  selectCartItemDisplayGroups,
  selectCartItemDisplayGroupsCount,
  selectProductNameByItemId,
  selectCustomDisplayAttributesByItemId,
  selectProductKeyByItemId,
  selectProductVersionByItemId,
  selectPreviewUrlByItemId,
  selectQuantityByItemId,
  selectBlockUserInteractionByItemId,
  selectErrorByItemId,
  selectFulfillmentDataByItemId,
  selectFulfillmentSkuByItemId,
  selectFulfillmentSkuVersionByItemId,
  selectHasItems,
  selectMerchandisableQuantitiesV2,
  selectEditDesignUrl,
  selectDocumentRefUrl,
  selectAnalyticsDataByItemId,
  selectFinanceDataByItemId,
  selectProductAttributeList,
  selectDesignDataByItemId,
  selectProductAttributes,
  selectEditOptionsUrl,
  selectPurcsDataByItemId,
  selectSuppressDefaultGroupDisplay,
  selectItemGroupTitle,
  selectItemGroupTotalLabel,
  selectItemUpdateInProgressByItemId,
  selectAreAnyItemsBlockingUserInteraction,
  selectProductNamesForItemIds,
  selectCartContainsSubscription,
  selectItemsWithLegalRequirements,
  selectCartItemsMap,
  selectAreAnySpecificItemsBlockingUserInteraction,
};
